import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ActivitySelectControl } from '@/module/Nomen/Activity/ActivitySelectControl';
import { AwardInstitutionSelectControl } from '@/module/Nomen/AwardInstitution/AwardInstitutionSelectControl';
import { AwardMembershipSelectControl } from '@/module/Nomen/AwardMembership/AwardMembershipSelectControl';
import { AwardTypeSelectControl } from '@/module/Nomen/AwardType/AwardTypeSelectControl';
import { AwardedForSelectControl } from '@/module/Nomen/AwardedFor/AwardedForSelectControl';
import { ContributionSelectControl } from '@/module/Nomen/Contribution/ContributionSelectControl';

import { AuthorListControl, Content, Form, FormSubmit, NumberInputControl, TextInputControl } from '@/core/components';

import { AwardSchema } from './model/AwardSchema';

interface AwardFormProps extends FormSubmit<AwardSchema> {
  title: string;
}

export function AwardForm(props: AwardFormProps) {
  const { title, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();
  const form = useForm<AwardSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content type="full">
            <TextInputControl<AwardSchema>
              name="title"
              label={t('awards.form.fields.title')}
              isSubmitting={isSubmitting}
            />
            <Group>
              <NumberInputControl<AwardSchema>
                name="year"
                label={t('awards.form.fields.year')}
                isSubmitting={isSubmitting}
                allowDecimal={false}
              />
              <AwardedForSelectControl<AwardSchema>
                name="awardedFor"
                label={t('awards.form.fields.awardedFor')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <Group>
              <AwardTypeSelectControl<AwardSchema>
                name="type"
                label={t('awards.form.fields.type')}
                isSubmitting={isSubmitting}
              />
              <AwardInstitutionSelectControl<AwardSchema>
                name="institution"
                label={t('awards.form.fields.institution')}
                isSubmitting={isSubmitting}
              />
            </Group>
            <Group>
              <ContributionSelectControl<AwardSchema>
                name="contribution"
                label={t('awards.form.fields.contribution')}
                isSubmitting={isSubmitting}
              />
              <ActivitySelectControl<AwardSchema>
                name="activity"
                label={t('awards.form.fields.activity')}
                isSubmitting={isSubmitting}
              />
            </Group>
          </Content>
          <Content type="full">
            <AuthorListControl<AwardSchema>
              name="authors"
              isSubmitting={isSubmitting}
              label={t('authors.form.title')}
              renderMembership={({ membershipName }) => (
                <AwardMembershipSelectControl
                  label={t('authors.form.fields.membership')}
                  name={membershipName}
                  isSubmitting={isSubmitting}
                  isDisabled={isSubmitting}
                />
              )}
            />
          </Content>
        </Stack>
      </Form>
    </FormProvider>
  );
}
